import React, { useEffect, useRef, useState } from 'react';
import { TextField, InputAdornment, Drawer } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSelector } from 'react-redux';
import PageBackBtn from '../Buttons/PageBackBtn';
import styles from './GlobalSearch.module.css';
import { useDispatch } from 'react-redux';
import {
  getGlobalSearch,
  resetConvertedContactId,
  resetGlobalSearchModalState,
  resetGlobalSearchsData,
  setGlobalSearchModalState,
} from '../../pages/app/Manage/InstituteProfile/service/instituteSlice';
import {
  checkAccess,
  checkSubDomainAccess,
  getSubDomainAccessStatus,
} from '../RolePermissionAccess/RolePermissionAccess';
import { useAuthUser } from 'react-auth-kit';
import { getCourse, highlightMatch } from '../CommonFunctions/CommonFunctions';
import { openLeadExpandModal } from '../LeadPopup/hooks/useLeadCard';
import { openContactExpandModal } from '../ViewContact/hooks/useViewContact';
import searchIcon from '../../assets/images/search-unhappy.svg';
import Loader from '../Loader/Loader';
import { setToastNotification } from '../ToastNotification/service/toastNotificationSlice';
import NoAccess from '../NoAccess/NoAccess';
import { clearLeadBoardRefresh } from '../../service/websocketNotificationsSlice';
import { resetDeletedLeadId } from '../../service/activeInstituteSlice';

const categories = [
  { label: 'All', value: 'ALL' },
  { label: 'Leads', value: 'LEADS' },
  { label: 'Students', value: 'STUDENTS' },
  { label: 'Contacts', value: 'CONTACTS' },
];

function GlobalSearch() {
  const auth = useAuthUser();
  const authUser = auth();
  const dispatch = useDispatch();
  const { userById } = useSelector((state: any) => state.userDetails);

  const hasAllBranchAccess = getSubDomainAccessStatus(
    'GLOBAL_PERMISSIONS',
    'BRANCH_ACCESS',
    'ALL_BRANCHES',
    userById?.rolePermission
  );
  const hasSelfAssignedLeadOnly = getSubDomainAccessStatus(
    'NURTURE_BOARD',
    'LEAD_ACCESS',
    'ASSIGNED_ONLY',
    userById?.rolePermission
  );
  const hasLeadCardPermission = checkSubDomainAccess(
    'NURTURE_BOARD',
    'LEAD_CARD',
    userById?.rolePermission
  );

  const hasContact = checkSubDomainAccess(
    'INBOUND',
    'CONTACTS',
    userById?.rolePermission
  );
  const hasStudent = checkAccess('STUDENTS', userById?.rolePermission);

  const isCompress = useSelector((state: any) => state.compress.isCompress);
  const {
    globalSearchsData,
    globalSearchLoading,
    globalSearchModalState,
    convertToLeadSuccess,
    convertedContactId,
  } = useSelector((state: any) => state.institute);
  const { courses } = useSelector((state: any) => state.courses);
  const { leadUpdateNotify } = useSelector(
    (state: any) => state?.websocketNotifications
  );
  const { deletedLeadId } = useSelector((state: any) => state.activeInstitute);
  const [active, setActive] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [debouncedValue, setDebouncedValue] = useState<string>(search);

  const requestIdRef = useRef(0);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const handleClick = () => {
    dispatch(setGlobalSearchModalState());
    dispatch(resetGlobalSearchsData());
    setActive(0);
    setSearch('');
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set('globalSearch', 'true');
    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    window.history.pushState({}, '', newUrl);
  };

  const onClose = () => {
    dispatch(resetGlobalSearchModalState());
    setSearch('');
    setActive(0);
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.delete('globalSearchTerm');
    queryParams.delete('globalSearch');
    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    window.history.pushState({}, '', newUrl);
  };

  const getEmail = (item: any) =>
    item?.entitytype === 'ENQUIRY' ? item?.studentEmail : item?.email;

  const getContact = (item: any) => {
    if (item?.entitytype === 'ENQUIRY') {
      if (item?.studentContact) {
        const ccLength = item?.contactCountryCode?.length;
        const contact = `+${
          item?.contactCountryCode
        } ${item?.studentContact?.slice(ccLength)}`;

        return contact;
      } else {
        return '';
      }
    }

    if (item?.contactNumber) {
      const isStudent = item?.entitytype === 'student';
      const countryCode = isStudent
        ? item?.contactCountryCode
        : item?.contactNoCountryCode;

      const codeLength = isStudent
        ? item?.contactCountryCode?.length
        : String(item?.contactNoCountryCode)?.length;

      if (
        item?.enquirySource === 'EXCEL_IMPORT' ||
        item?.enquirySource === 'Excel Import'
      ) {
        const contact = `+${countryCode} ${item?.contactNumber}`;
        return contact;
      } else {
        const contact = `+${countryCode} ${item?.contactNumber?.slice(
          codeLength
        )}`;

        return contact;
      }
    } else {
      return '';
    }
  };
  const filteredContactResults = convertedContactId
    ? globalSearchsData?.contactResults?.filter(
        (contact: any) => contact.id !== convertedContactId
      )
    : globalSearchsData?.contactResults;
  const filteredLeads = deletedLeadId
    ? globalSearchsData?.enquiryResults?.filter(
        (item: any) => item.id !== deletedLeadId
      )
    : globalSearchsData?.enquiryResults;
  const filteredStudents = deletedLeadId
    ? globalSearchsData?.studentResults?.filter(
        (item: any) => item.id !== deletedLeadId
      )
    : globalSearchsData?.studentResults;
  const searchData =
    active === 1
      ? filteredLeads
      : active === 2
      ? filteredStudents
      : active === 3
      ? filteredContactResults
      : [
          ...(filteredLeads || []),
          ...(filteredContactResults || []),
          ...(filteredStudents || []),
        ];

  const runGlobalSearch = (term: string) => {
    if (!term?.trim()) return;

    const body = {
      globalSearch: true,
      searchText: term,
      instituteId: authUser?.institute[0]?.instituteId,
      branchId: hasAllBranchAccess ? '' : userById?.branchId,
      assignedToId: hasSelfAssignedLeadOnly ? userById?.id : '',
      entityName: ['ENQUIRY', 'STUDENT_CONTACT', 'BATCHSTUDENTMAPPING'],
    };

    const currentRequestId = ++requestIdRef.current;
    dispatch<any>(getGlobalSearch({ data: body, requestId: currentRequestId }));
  };

  useEffect(() => {
    if (globalSearchModalState) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 200);
    }
  }, [globalSearchModalState]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const globalSearchTerm = urlSearchParams.get('globalSearchTerm');
    const globalSearch = urlSearchParams.get('globalSearch');

    if (globalSearch === 'true') {
      dispatch(setGlobalSearchModalState());
    }

    if (globalSearchTerm) {
      setSearch(globalSearchTerm);
      runGlobalSearch(globalSearchTerm);
    } else {
      setSearch('');
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(search.trim());
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (debouncedValue) {
      params.set('globalSearchTerm', debouncedValue);
      runGlobalSearch(debouncedValue);
    } else {
      params.delete('globalSearchTerm');
      dispatch(resetGlobalSearchsData());
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }, [debouncedValue]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const globalSearchTerm = params.get('globalSearchTerm');
    if (!globalSearchTerm && searchData?.length > 0) {
      dispatch(resetGlobalSearchsData());
    }
  }, [searchData]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const globalSearchTerm = urlSearchParams.get('globalSearchTerm');
    if (
      // (convertToLeadSuccess !== true && convertToLeadSuccess !== false) ||
      leadUpdateNotify === true
    ) {
      if (globalSearchTerm) runGlobalSearch(globalSearchTerm);

      if (leadUpdateNotify === true) {
        dispatch(resetConvertedContactId());
        dispatch(clearLeadBoardRefresh());
        dispatch(resetDeletedLeadId());
      }
    }
  }, [leadUpdateNotify]);
  return (
    <div>
      <TextField
        type="search"
        placeholder={'Search'}
        fullWidth
        style={{
          borderRadius: '4px',
          border: 'none',
          outline: 'none',
          height: '34px',
          width: '280px',
        }}
        size="small"
        onClick={handleClick}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: '20px' }} />
            </InputAdornment>
          ),
          sx: {
            input: {
              padding: '6px 14px 6px 2px',
            },
          },
        }}
        inputProps={{
          readOnly: true,
        }}
      />
      <Drawer
        anchor={'right'}
        open={globalSearchModalState}
        onClose={onClose}
        hideBackdrop
        disableEnforceFocus
        sx={{
          height: '100vh',
          zIndex: 99999,
          width: isCompress ? 'calc(100% - 70px)' : 'calc(100% - 200px)',

          position: 'relative',
          '& .MuiDrawer-paper': {
            overflowY: 'unset',
            width: isCompress ? 'calc(100% - 70px)' : 'calc(100% - 200px)',
          },
        }}
      >
        <div className={styles.gloabalSearchWrapper}>
          <div className={styles.gloabalSearchHead}>
            <PageBackBtn text={false} onClick={onClose} />
            <TextField
              type="search"
              placeholder={'Search'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              style={{
                padding: '0px',
                borderRadius: '4px',
                border: 'none',
                outline: 'none',
                background: '#F9F9F9',
                color: '#142C44',
                fontSize: '14px',
              }}
              size="small"
              InputProps={{
                inputRef: searchInputRef,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div className={styles.globalSearchTabsWrap}>
            <div className={styles.globalSearchDesc}>I’m looking for...</div>
            <ul className={styles.globalSearchTabs}>
              {categories?.map((item: any, index: number) => (
                <li
                  className={`${styles.globalSearchTab} ${
                    active === index ? styles.activeTab : ''
                  }`}
                  onClick={() => setActive(index)}
                  key={index}
                >
                  {item?.label}
                </li>
              ))}
            </ul>
          </div>
          <ul className={styles.globalSearchResultsWrap}>
            {globalSearchLoading ? (
              <li>
                <Loader />
              </li>
            ) : (
              <>
                {searchData?.length > 0 ? (
                  searchData?.map((item: any, index: number) => (
                    <li
                      className={styles.globalSearchResult}
                      key={index}
                      onClick={
                        item?.entitytype !== 'student_contact'
                          ? () =>
                              hasLeadCardPermission
                                ? openLeadExpandModal(
                                    item?.instituteId,
                                    item?.entitytype === 'student'
                                      ? item?.contactNumber
                                      : item?.studentContact,
                                    item?.entitytype === 'student'
                                      ? item?.email
                                      : item?.studentEmail,
                                    item?.entitytype === 'student'
                                      ? item?.enquiryId
                                      : item?.id,
                                    dispatch,
                                    'overview'
                                  )
                                : dispatch(
                                    setToastNotification({
                                      message: String(
                                        'You do not have permission to access the lead card'
                                      ),
                                      type: 'error',
                                      snackOpen: true,
                                    })
                                  )
                          : () => openContactExpandModal(item?.id, dispatch)
                      }
                    >
                      <div className={styles.globalSearchResultLead}>
                        <div className={styles.globalSearchLeadLeft}>
                          <div className={styles.globalSearchLeadAvatar}>
                            {item?.entitytype === 'ENQUIRY'
                              ? item?.studentFirstName?.charAt(0)
                              : item?.firstName?.charAt(0)}
                          </div>
                        </div>
                        <div className={styles.globalSearchLeadRight}>
                          <div className={styles.globalSearchLeadRightTop}>
                            <div className={styles.globalSearchLeadName}>
                              {item?.entitytype === 'ENQUIRY' ? (
                                <>
                                  {highlightMatch(
                                    item?.studentFirstName ?? '',
                                    search
                                  )}{' '}
                                  {highlightMatch(
                                    item?.studentLastName ?? '',
                                    search
                                  )}
                                </>
                              ) : (
                                <>
                                  {highlightMatch(
                                    item?.firstName ?? '',
                                    search
                                  )}{' '}
                                  {highlightMatch(item?.lastName ?? '', search)}
                                </>
                              )}
                            </div>
                            <div className={styles.globalSearchLeadTag}>
                              {item?.entitytype === 'ENQUIRY'
                                ? 'Lead'
                                : item?.entitytype === 'student_contact'
                                ? 'Contact'
                                : item?.entitytype === 'student'
                                ? 'Student'
                                : ''}
                            </div>
                          </div>
                          <div className={styles.globalSearchLeadRightBottom}>
                            {highlightMatch(getEmail(item) ?? '', search)}
                            {getEmail(item) && getContact(item) && ' | '}
                            {highlightMatch(getContact(item) ?? '', search)}
                          </div>
                        </div>
                      </div>
                      <div className={styles.globalSearchResultLeadCourse}>
                        <div
                          className={styles.globalSearchResultLeadCourseLabel}
                        >
                          Course:
                        </div>
                        <div
                          className={styles.globalSearchResultLeadCourseValue}
                        >
                          {getCourse(item?.courseId, courses)?.courseName ??
                            '-'}{' '}
                          {item?.duplicateCount > 0 && (
                            <span
                              className={
                                styles.globalSearchResultLeadCourseValueCount
                              }
                            >
                              +{item?.duplicateCount}
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  ))
                ) : (
                  <li className={styles.globalSearchNoData}>
                    {(active === 3 && !hasContact) ||
                    (active === 2 && !hasStudent) ? (
                      <NoAccess
                        title="You don't have permission to view contacts"
                        desc="Either go back to other tabs or log in as a different user."
                      />
                    ) : (
                      <>
                        {search?.length > 0 && (
                          <>
                            <img src={searchIcon} alt="icon" />
                            <div className={styles.globalSearchNoDataTitle}>
                              Oops! No result Found, but let's dig deeper.
                            </div>
                          </>
                        )}
                        <div className={styles.globalSearchNoDataDesc}>
                          {search?.length > 0
                            ? 'Give it another shot with a different keyword and see nurture works its magic!'
                            : 'Give it a shot with different keyword and see nurture works its magic!'}
                        </div>
                      </>
                    )}
                  </li>
                )}
              </>
            )}
          </ul>
        </div>
      </Drawer>
    </div>
  );
}

export default GlobalSearch;
